/* Subscription Data Service
 * - Retrieves details for subscriptions
 * - Performs CRUD operations on recipients WRT subscriptions
 * - Provides common interface for all subscription types
 *
 * Angular module: trackerbase-app.services.subscription-data-service
 * Service Name: SubscriptionData
 */

import angular from "angular";

import Subscription from "../models/Subscription";
import Assert from "tbAssert";

import SubscriptionDataServicesModule from "./subscription-data-services";

const subscriptionTypes = Subscription.GetSubscriptionTypes();

const mHttpSvc = new WeakMap();
const mQSvc = new WeakMap();
const mLogSvc = new WeakMap();

const mTrackingTargetSubscriptionDataSvc = new WeakMap();
const mStateEventSubscriptionDataSvc = new WeakMap();
const mStateOfficeSubscriptionDataSvc = new WeakMap();
const mOfficeSubscriptionDataSvc = new WeakMap();
const mOfficeTypeSubscriptionDataSvc = new WeakMap();
const mElectionSubscriptionDataSvc = new WeakMap();

const targetRetrievalServicesMap = new Map([
    ["Target", mTrackingTargetSubscriptionDataSvc],
    ["StateId", mStateOfficeSubscriptionDataSvc],
    ["StateEvent", mStateEventSubscriptionDataSvc],
    ["Office", mOfficeSubscriptionDataSvc],
    ["Election", mElectionSubscriptionDataSvc],
    ["OfficeType", mOfficeTypeSubscriptionDataSvc]
]);

function getTargetRetrievalService(serviceName) {
    let targetRetrievalService = targetRetrievalServicesMap.get(serviceName);

    if (targetRetrievalService != null) {
        return targetRetrievalService;
    }

    throw new TypeError(`Invalid Subscription Service Type Name: "${serviceName}"`);
}

const mSubscriptionTargetCache = new WeakMap();

class SubscriptionDataService {
    constructor(
        $http,
        $q,
        $log,
        TrackingTargetSubscriptionData,
        StateEventSubscriptionData,
        StateOfficeSubscriptionData,
        OfficeSubscriptionData,
        OfficeTypeSubscriptionData,
        ElectionSubscriptionData
    ) {
        Assert.StrictAssert($http != null, "Assertion Failure: Missing Http Service Dependency");
        Assert.StrictAssert($q != null, "Assertion Failure: Missing Q Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");

        Assert.StrictAssert(TrackingTargetSubscriptionData != null, "Assertion Failure: Missing Tracking Target Subscription Service Dependency");
        Assert.StrictAssert(StateEventSubscriptionData != null, "Assertion Failure: Missing State Event Subscription Service Dependency");
        Assert.StrictAssert(StateOfficeSubscriptionData != null, "Assertion Failure: Missing State Office Subscription Service Dependency");
        Assert.StrictAssert(OfficeSubscriptionData != null, "Assertion Failure: Missing Office Subscription Service Dependency");
        Assert.StrictAssert(OfficeTypeSubscriptionData != null, "Assertion Failure: Missing Office Type Subscription Service Dependency");
        Assert.StrictAssert(ElectionSubscriptionData != null, "Assertion Failure: Missing Election Subscription Service Dependency");

        mHttpSvc.set(this, $http);
        mQSvc.set(this, $q);
        mLogSvc.set(this, $log);

        mTrackingTargetSubscriptionDataSvc.set(this, TrackingTargetSubscriptionData);
        mStateEventSubscriptionDataSvc.set(this, StateEventSubscriptionData);
        mStateOfficeSubscriptionDataSvc.set(this, StateOfficeSubscriptionData);
        mOfficeSubscriptionDataSvc.set(this, OfficeSubscriptionData);
        mOfficeTypeSubscriptionDataSvc.set(this, OfficeTypeSubscriptionData);
        mElectionSubscriptionDataSvc.set(this, ElectionSubscriptionData);

        mSubscriptionTargetCache.set(this, new Map());
    }

    toString() {
        return "Subscription Data Service";
    }

    getSubscriptionTypesInfo() {
        return subscriptionTypes;
    }

    getSubscriptionTarget(queriedSub) {
        let targetRetrievalService = getTargetRetrievalService(queriedSub.subscriptionType).get(this);

        let subTargetCacheId = `${queriedSub.subscriptionType}${queriedSub.subscriptionTypeId}`;
        let subTargetCachedItem = mSubscriptionTargetCache.get(this).get(subTargetCacheId);

        if (angular.isDefined(subTargetCachedItem)) {
            let current = new Date();

            //5 trillion nano-seconds => 5000 milliseconds => 5 seconds.
            if ((current.getTime() - subTargetCachedItem.cachedAt.getTime()) < 5000000000000 ) {
                return mQSvc.get(this).when(subTargetCachedItem.result);
            }
        }

        //Result not in the cache, or cached result is too old.
        //Retrieve the subscription target from the server.

        let subTargetPromise =
            targetRetrievalService
                .getTarget(queriedSub.subscriptionTargetId)
                .then(
                    (subscriptionTarget) => {
                        let cacheId = subTargetCacheId;
                        mSubscriptionTargetCache.get(this).set(
                            cacheId,
                            {
                                result: subscriptionTarget,
                                cachedAt: new Date()
                            }
                        );

                        return subscriptionTarget;
                    },
                    (errorResponse) => {
                        mQSvc.get(this).reject(errorResponse);
                    }
                );

        return subTargetPromise;
    }

    getSubscriptionTargetBatch(queriedSubs) {
        let targetRetrievalService = getTargetRetrievalService(queriedSubs.subscriptionType).get(this);

        let targetBatchIds = queriedSubs.subscriptions.map((qSub) => qSub.subscriptionTargetId);

        let subTargetsBatchPromise =
            targetRetrievalService
                .getTargetBatch(targetBatchIds)
                .then(
                    (subscriptionTargets) => subscriptionTargets,
                    (errorResponse) => mQSvc.get(this).reject(errorResponse)
                );

        return subTargetsBatchPromise;
    }

    _changeSubscription(subscriber, subToChange, subChangeFn) {

        let subChangeService = getTargetRetrievalService(subToChange.subscriptionType).get(this);

        let subChangePromiseFn = subChangeFn(subChangeService);

        //Use call to set "this" properly inside the change service implementation's method call.
        let subChangePromise = subChangePromiseFn.call(
                subChangeService,
                subscriber.recipientId,
                subToChange.subscriptionTargetId
            )
            .then(
                (succeeded) => (succeeded ? true : mQSvc.get(this).reject("Failed to add subscription")),
                (rejectResponse) => mQSvc.get(this).reject(rejectResponse)
            );

        return subChangePromise;
    }

    addSubscription(subscriber, subToAdd) {
        return this._changeSubscription(
            subscriber,
            subToAdd,
            (chgService) => chgService.addSubscription
        );
    }

    removeSubscription(subscriber, subToDelete) {
        return this._changeSubscription(
            subscriber,
            subToDelete,
            (chgService) => chgService.removeSubscription
        );
    }
}

SubscriptionDataService.$inject = [
    "$http",
    "$q",
    "$log",
    "TrackingTargetSubscriptionData",
    "StateEventSubscriptionData",
    "StateOfficeSubscriptionData",
    "OfficeSubscriptionData",
    "OfficeTypeSubscriptionData",
    "ElectionSubscriptionData"
];

export default angular
    .module("trackerbase-app.services.subscription-data-service",[
        SubscriptionDataServicesModule
    ])
    .service("SubscriptionData", SubscriptionDataService)
    .name;



//////////////////
// WEBPACK FOOTER
// ./src/services/SubscriptionDataService.js
// module id = LFzH
// module chunks = 1